import { memo, useEffect, useState } from 'react';
import { ownGroups } from '@/game/view/groups';
import { labelOrDefault, nameOf } from '@/game/view/names';
import {
  engagedIds,
  isSlotSelection,
  liveSlot,
  SLOT_COUNT,
  type SlotStatus,
  type SlotType,
  slotComposition,
  slotHp,
  slotNameKey,
  slotStatus,
} from '@/game/view/slots';
import { tickEngagements } from '@/game/view/tickCache';
import { useSimFrame } from '../app/simFrame';
import { InlineRename } from '../InlineRename';
import { cancelRename, commitRename, startRename, type UiStore, useUi } from '../ui/store';

const STATUS_DOT: Record<SlotStatus, string> = {
  fighting: 'bg-danger shadow-[0_0_6px_var(--color-danger)]',
  moving: 'bg-accent',
  idle: 'bg-ok',
};

const kbd =
  'inline-grid h-4 min-w-4 place-items-center rounded border border-line bg-fill px-1 text-[10px] font-medium tabular-nums text-fg-muted';

/**
 * Publishes the bar's height (plus a gap) as `--groups-bar-h` on the HUD root,
 * which `hud-dock` (index.css) lifts itself by, so the dock never sits under
 * the bar and the bar stays put when the dock comes and goes.
 */
function useReserveHeight(bar: HTMLElement | null) {
  useEffect(() => {
    const root = bar?.parentElement;
    if (!bar || !root) return;
    const publish = () => root.style.setProperty('--groups-bar-h', `${bar.offsetHeight + 6}px`);
    publish();
    const resize = new ResizeObserver(publish);
    resize.observe(bar);
    return () => {
      resize.disconnect();
      root.style.removeProperty('--groups-bar-h');
    };
  }, [bar]);
}

/** What one tile shows: a control slot, or a fallback (empty slot N whose digit acts on formation Group N). */
interface TileProps {
  n: number;
  /** Rename key in the names map: `slot:N`, or the formation group's id for a fallback. */
  nameId: string;
  /** The player's own name, '' without. */
  name: string;
  label: string;
  fallback: boolean;
  selected: boolean;
  renaming: boolean;
  status: SlotStatus;
  hp: number;
  maxHp: number;
  comp: SlotType[];
  count: number;
}

const sameTile = (a: TileProps & Handlers, b: TileProps & Handlers): boolean =>
  a.n === b.n &&
  a.nameId === b.nameId &&
  a.name === b.name &&
  a.label === b.label &&
  a.fallback === b.fallback &&
  a.selected === b.selected &&
  a.renaming === b.renaming &&
  a.status === b.status &&
  a.hp === b.hp &&
  a.maxHp === b.maxHp &&
  a.count === b.count &&
  a.ui === b.ui &&
  a.onDigit === b.onDigit &&
  a.comp.length === b.comp.length &&
  a.comp.every((c, i) => c.type === b.comp[i].type && c.count === b.comp[i].count);

interface Handlers {
  ui: UiStore;
  onDigit: (number: number, additive: boolean) => void;
}

/** One tile; re-renders only when something it shows changed (the bar renders every tick). */
const Tile = memo(function Tile(t: TileProps & Handlers) {
  const { ui } = t;
  const body = (
    <>
      <span className="flex w-full items-center gap-1.5">
        <span className={`size-[7px] shrink-0 rounded-full ${STATUS_DOT[t.status]}`} />
        {t.renaming ? (
          <InlineRename
            value={t.name}
            label={`Rename ${t.fallback ? 'Group' : 'slot'} ${t.n}`}
            onCommit={(name) => commitRename(ui, t.nameId, name)}
            onCancel={() => cancelRename(ui)}
            className="h-5 flex-1 text-xs"
          />
        ) : (
          <span
            className={`min-w-0 flex-1 truncate text-xs ${t.name ? 'font-medium' : 'text-fg-muted'}`}
          >
            {t.label}
          </span>
        )}
        {t.fallback && (
          <span className="text-[10px] tabular-nums text-fg-subtle" title="Formation group">
            G{t.n}
          </span>
        )}
        <span className={kbd}>{t.n}</span>
      </span>
      <span className="flex flex-wrap gap-x-1.5 text-[10px] tabular-nums text-fg-muted">
        {t.comp.map((c) => (
          <span key={c.type}>
            <b className="font-medium text-fg">{c.glyph}</b>×{c.count}
          </span>
        ))}
      </span>
      <span className="h-[3px] w-full overflow-hidden rounded-full bg-white/20">
        <i
          className="block h-full bg-ok"
          style={{ width: `${t.maxHp > 0 ? Math.min(100, (t.hp / t.maxHp) * 100) : 0}%` }}
        />
      </span>
      <span className="sr-only">
        {t.count} units, {t.hp} of {t.maxHp} hp, {t.status}
      </span>
    </>
  );
  const cls = `card w-32 shrink-0 gap-1 px-2 py-1.5 ${t.selected ? 'bg-fill-hover ring-1 ring-accent/70' : ''} ${t.fallback ? 'opacity-60' : ''}`;
  if (t.renaming) {
    return <div className={cls}>{body}</div>;
  }
  return (
    <button
      type="button"
      aria-label={`${t.label}, key ${t.n}${t.fallback ? ', formation group' : ''}: ${t.count} units, ${t.hp} of ${t.maxHp} hp, ${t.status}`}
      aria-pressed={t.selected}
      title={
        t.fallback
          ? `Key ${t.n} selects formation Group ${t.n} (no control group ${t.n} yet). Ctrl+${t.n} assigns the selection. Double-click or F2 renames`
          : `Key ${t.n} selects it (shift adds; twice goes there). Ctrl+${t.n} replaces it with the selection. Double-click or F2 renames`
      }
      // The second click of a double-click renames: it must not also pan.
      onClick={(e) => {
        if (e.detail > 1) return;
        t.onDigit(t.n, e.shiftKey);
      }}
      onDoubleClick={() => startRename(ui, t.nameId, 'slot')}
      className={cls}
    >
      {body}
    </button>
  );
}, sameTile);

/**
 * Control groups bar: numbered tiles for the slots in use (name, unit
 * composition, combined hp, a status dot), the formation groups an empty
 * slot's digit falls back to (dimmed, tagged "G3"), and one empty hint tile.
 * Click recalls (shift adds, again goes there); double-click renames
 * (`InlineRename`, names are client-local). Sits bottom-centre; the dock lifts itself above it (`--groups-bar-h`).
 */
export function GroupsBar({
  ui,
  selectedUnitIds,
  onDigit,
}: {
  ui: UiStore;
  selectedUnitIds: readonly string[];
  onDigit: (number: number, additive: boolean) => void;
}) {
  const [bar, setBar] = useState<HTMLElement | null>(null);
  useReserveHeight(bar);
  const frame = useSimFrame();
  const { view: state, playerId, rules, terrain } = frame;
  const slots = useUi(ui, (s) => s.slots);
  const names = useUi(ui, (s) => s.names);
  const renaming = useUi(ui, (s) => s.renaming);
  const groups = ownGroups(state, playerId);
  const engaged = engagedIds(tickEngagements(state, { terrain, rules }));

  const tiles: TileProps[] = [];
  let hint: number | null = null;
  for (let n = 1; n <= SLOT_COUNT; n++) {
    const slotUnits = liveSlot(state, playerId, slots, n);
    const group = slotUnits.length === 0 ? groups.find((g) => g.number === n) : undefined;
    const units = group ? group.units : slotUnits;
    if (units.length === 0) {
      if (hint === null) hint = n;
      continue;
    }
    const nameId = group ? group.id : slotNameKey(n);
    const hp = slotHp(rules, units);
    tiles.push({
      n,
      nameId,
      name: nameOf(names, nameId) ?? '',
      label: labelOrDefault(names, nameId, `Group ${n}`),
      fallback: !!group,
      selected: isSlotSelection(units, selectedUnitIds),
      renaming: renaming?.site === 'slot' && renaming.id === nameId,
      status: slotStatus(units, engaged),
      hp: hp.hp,
      maxHp: hp.max,
      comp: slotComposition(rules, units),
      count: units.length,
    });
  }
  if (tiles.length === 0 && hint === null) return null;

  return (
    <div
      ref={setBar}
      className="pointer-events-none absolute bottom-3 left-1/2 z-10 flex w-[calc(100vw-34.5rem)] min-w-[20rem] -translate-x-1/2 flex-col items-center gap-1"
    >
      <div className="panel pointer-events-auto flex max-w-full flex-col gap-1 p-1.5">
        <div className="flex gap-1.5 overflow-x-auto">
          {tiles.map((t) => (
            <Tile key={t.n} {...t} ui={ui} onDigit={onDigit} />
          ))}
          {hint !== null && (
            <div className="flex w-36 shrink-0 flex-col gap-1 rounded-xl border border-line px-2 py-1.5 text-[10px] text-fg-subtle opacity-70">
              <span className="flex items-center gap-1.5 text-xs">
                <span className="size-[7px] shrink-0 rounded-full bg-fg-subtle" />
                <span className="flex-1">Empty</span>
                <span className={kbd}>{hint}</span>
              </span>
              <span className="whitespace-nowrap leading-snug">
                Select units, then <span className={kbd}>Ctrl</span>+
                <span className={kbd}>{hint}</span>
              </span>
            </div>
          )}
        </div>
        <p className="flex flex-wrap justify-center [@media(max-height:700px)]:hidden gap-x-3 text-[10px] text-fg-subtle">
          <span>
            <span className={kbd}>Ctrl</span>+<span className={kbd}>1-9</span> assign
          </span>
          <span>
            <span className={kbd}>1-9</span> recall
          </span>
          <span>
            <span className={kbd}>1-9</span>×2 pan
          </span>
          <span>
            <span className={kbd}>G</span> formation
          </span>
          <span>
            <span className={kbd}>U</span> ungroup
          </span>
          <span>
            <span className={kbd}>F2</span> rename
          </span>
        </p>
      </div>
    </div>
  );
}
